import {
  BOARD_ANSWER_MAX, BOARD_MAX_PEOPLE, BOARD_MIN_ANSWERS, BOARD_SECTIONS, answeredBy, boardAnswers, boardCoverage,
  type Board, type BoardQuestionId, type Entrevistado,
} from '@ads/shared';
import { useEffect, useState, type ReactNode } from 'react';
import { TextField } from '../components/Field';
import * as Ic from '../components/icons';
import { useAnalyzeBoard, useBoardReading } from '../lib/queries';
import { Insights } from './InicioPanels';

/* ─────────────────────────────────────────────────────────────────────
   06 · Leitura executiva do Board — a visão da liderança sobre o
   negócio. Uma entrevista por pessoa, as mesmas 16 perguntas; as
   respostas de todos lado a lado quando se quer comparar; e, pedida
   com um botão, a leitura consolidada que entra no contexto de todos
   os agentes.
   ───────────────────────────────────────────────────────────────────── */

type Details = React.DetailsHTMLAttributes<HTMLDetailsElement> & { onToggle: (e: React.SyntheticEvent<HTMLDetailsElement>) => void };

const initials = (name: string) => name.trim().split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]!.toUpperCase()).join('') || '·';
const personLabel = (e: Entrevistado, i: number) => e.name.trim() || `Entrevistado ${i + 1}`;
const newId = () => (globalThis.crypto?.randomUUID?.() ?? `p-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`);

export function BoardBlock({ details, board, onChange, onRead }: {
  details: Details;
  board: Board;
  onChange: (b: Board) => void;
  onRead: () => void;
}) {
  const people = board.entrevistados;
  const [sel, setSel] = useState<string | null>(people[0]?.id ?? null);
  const [confirmRm, setConfirmRm] = useState(false);
  const current = people.find(p => p.id === sel) ?? people[0] ?? null;
  const idx = current ? people.indexOf(current) : -1;
  const cov = boardCoverage(board);

  useEffect(() => { if (!confirmRm) return; const t = setTimeout(() => setConfirmRm(false), 3500); return () => clearTimeout(t); }, [confirmRm]);

  const patchPerson = (id: string, patch: Partial<Entrevistado>) =>
    onChange({ entrevistados: people.map(p => (p.id === id ? { ...p, ...patch } : p)) });
  const setAnswer = (q: BoardQuestionId, v: string) => current && patchPerson(current.id, { answers: { ...current.answers, [q]: v } });
  const add = () => {
    const p: Entrevistado = { id: newId(), name: '', role: '', answers: {} };
    onChange({ entrevistados: [...people, p] });
    setSel(p.id);
  };
  const remove = () => {
    if (!current) return;
    const rest = people.filter(p => p.id !== current.id);
    onChange({ entrevistados: rest });
    setSel(rest[Math.max(0, idx - 1)]?.id ?? null);
    setConfirmRm(false);
  };

  return (
    <details {...details}>
      <summary>
        <span className="no mono">06</span><span className="t">Leitura executiva do Board</span>
        <span className="st mono">
          <span className={`cnt-b${cov.done === cov.total ? ' done' : ''}`}><Ic.Check />{cov.done} de {cov.total}</span>
          <span className="tog" aria-hidden="true"><i /><i /></span>
        </span>
      </summary>
      <div className="body">
        <div className="bd-intro">
          <h4>A visão da liderança sobre o negócio</h4>
          <p>Para concluir o Setup, queremos compreender como a liderança enxerga a organização, seus desafios, prioridades e futuro. As respostas serão combinadas aos documentos, dados e demais informações fornecidas ao sistema para formar o contexto estratégico inicial da organização.</p>
          <p className="bd-tip"><Ic.Clock />As respostas podem ser objetivas. Sempre que possível, até 3 ou 4 linhas por pergunta.</p>
        </div>

        {!current ? (
          <div className="bd-empty">
            <button type="button" className="btn-ghost with-ic" onClick={add}><Ic.Plus />Começar a primeira entrevista</button>
            <span>Uma entrevista por pessoa da liderança — as mesmas 16 perguntas para todos. Dá para preencher durante a conversa ou depois.</span>
          </div>
        ) : (
          <>
            <div className="bd-people" role="tablist" aria-label="Entrevistados">
              {people.map((p, i) => {
                const n = answeredBy(p);
                return (
                  <button key={p.id} type="button" role="tab" aria-selected={p.id === current.id} className="bd-p" onClick={() => { setSel(p.id); setConfirmRm(false); }}>
                    <span className="bd-av">{initials(p.name)}</span>
                    <span className="bd-pn"><b>{personLabel(p, i)}</b><em>{p.role.trim() || 'Função ou cargo'}</em></span>
                    <span className={`bd-pc${n === 16 ? ' done' : ''}`}>{n}/16</span>
                  </button>
                );
              })}
              {people.length < BOARD_MAX_PEOPLE && (
                <button type="button" className="addlink bd-add" onClick={add}><Ic.Plus />Adicionar entrevistado</button>
              )}
            </div>

            <div className="bd-person" role="tabpanel">
              <div className="grid c2">
                <TextField id="bd-name" label="Nome" value={current.name} placeholder="Ex.: Ana Souza" maxLength={80}
                  onChange={v => patchPerson(current.id, { name: v })} onRead={onRead} />
                <TextField id="bd-role" label="Função / cargo" value={current.role} placeholder="Ex.: Diretora-geral" maxLength={80}
                  onChange={v => patchPerson(current.id, { role: v })} onRead={onRead} />
              </div>
              <div className="bd-person-foot">
                <span>{answeredBy(current)} de 16 respondidas por {personLabel(current, idx)}</span>
                {confirmRm
                  ? <button type="button" className="mat-del" onClick={remove}>Excluir esta entrevista</button>
                  : <button type="button" className="bd-rm" onClick={() => setConfirmRm(true)}><Ic.Close />Remover entrevista</button>}
              </div>
            </div>

            {BOARD_SECTIONS.map(sec => (
              <section className="bd-sec" key={sec.key}>
                <h5>{sec.title}</h5>
                {sec.questions.map(q => (
                  <Question key={`${current.id}-${q.id}`} id={q.id} text={q.text} hint={'hint' in q ? q.hint : undefined}
                    value={current.answers[q.id] ?? ''} who={personLabel(current, idx)}
                    others={people.filter(p => p.id !== current.id && (p.answers[q.id] ?? '').trim()).map(p => ({ who: personLabel(p, people.indexOf(p)), role: p.role, a: p.answers[q.id]! }))}
                    onChange={v => setAnswer(q.id, v)} onRead={onRead} />
                ))}
              </section>
            ))}
          </>
        )}

        <BoardReadingPanel board={board} />
      </div>
    </details>
  );
}

function Question({ id, text, hint, value, who, others, onChange, onRead }: {
  id: BoardQuestionId; text: string; hint?: string; value: string; who: string;
  others: Array<{ who: string; role: string; a: string }>;
  onChange: (v: string) => void; onRead: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [had, setHad] = useState(value.trim().length > 0);
  return (
    <div className="bq">
      <label className="bq-q" htmlFor={`bq-${id}`}>
        <span className="bq-n mono">{id.slice(1).padStart(2, '0')}</span>
        <span><b>{text}</b>{hint && <em>{hint}</em>}</span>
      </label>
      <div className="bq-a">
        <div className="ta">
          <textarea id={`bq-${id}`} value={value} maxLength={BOARD_ANSWER_MAX} placeholder={`Resposta de ${who}`}
            onChange={e => onChange(e.target.value)}
            onBlur={() => { const f = value.trim().length > 0; if (f && !had) onRead(); setHad(f); }} />
          <div className="bar">
            <span className="hint">
              {others.length > 0 && (
                <button type="button" className="bq-more" aria-expanded={open} onClick={() => setOpen(o => !o)}>
                  {open ? 'Esconder as outras respostas' : `Ver ${others.length === 1 ? 'a outra resposta' : `as outras ${others.length} respostas`}`}<Ic.ChevronDown />
                </button>
              )}
            </span>
            <span className="cnt mono"><b>{value.length}</b>/{BOARD_ANSWER_MAX}</span>
          </div>
        </div>
        {open && (
          <ul className="bq-others">
            {others.map((o, i) => <li key={i}><b>{o.who}{o.role.trim() ? <em> · {o.role}</em> : null}</b><p>{o.a}</p></li>)}
          </ul>
        )}
      </div>
    </div>
  );
}

/* ── a leitura da liderança ───────────────────────────────────────── */

function BoardReadingPanel({ board }: { board: Board }) {
  const q = useBoardReading();
  const analyze = useAnalyzeBoard();
  const total = boardAnswers(board);
  const eligible = total >= BOARD_MIN_ANSWERS;
  const d = q.data;
  const err = analyze.error instanceof Error ? analyze.error.message : null;

  if (!d) {
    if (analyze.isPending) {
      return (
        <div className="obj-panel is-loading bd-reading" aria-busy="true">
          <div className="obj-head"><h4><Ic.Layers />Leitura da liderança</h4><span className="obj-st busy"><span className="spin" />Lendo as entrevistas…</span></div>
          <div className="obj-skel"><i /><i /><i /></div>
        </div>
      );
    }
    return (
      <div className="obj-panel is-empty bd-reading">
        <span className="obj-ic"><Ic.Layers /></span>
        <p>{eligible
          ? 'Com as respostas em mãos, o ADS lê as entrevistas juntas e consolida a síntese, as prioridades, as convergências e as divergências da liderança.'
          : `Com pelo menos ${BOARD_MIN_ANSWERS} respostas, o ADS lê as entrevistas e consolida prioridades, convergências e divergências da liderança.`}
          {err && <span className="err-text"> {err}</span>}</p>
        {eligible && <button type="button" className="btn-ghost with-ic" onClick={() => analyze.mutate()}><Ic.Sparkle />Ler as entrevistas</button>}
      </div>
    );
  }

  const r = d.reading;
  const list = (title: string, icon: ReactNode, items: string[], empty: string, ordered = false) => (
    <div className="bd-col">
      <div className="bd-col-h">{icon}{title}</div>
      {items.length
        ? (ordered ? <ol>{items.map((x, i) => <li key={i}>{x}</li>)}</ol> : <ul>{items.map((x, i) => <li key={i}>{x}</li>)}</ul>)
        : <p className="muted-sm">{empty}</p>}
    </div>
  );

  return (
    <div className={`obj-panel bd-reading${d.stale ? ' is-stale' : ''}`} aria-live="polite">
      <div className="obj-head">
        <h4><Ic.Layers />Leitura da liderança</h4>
        {analyze.isPending
          ? <span className="obj-st busy"><span className="spin" />Lendo as entrevistas…</span>
          : d.stale
            ? <span className="obj-st">As respostas mudaram desde esta leitura
                {eligible && <button type="button" onClick={() => analyze.mutate()}><Ic.Refresh />Atualizar a leitura</button>}</span>
            : <span className="obj-st"><span title={d.basic ? 'Sem IA: modo demonstração, ou o modelo não respondeu.' : undefined}>
                {d.basic ? 'Leitura básica' : 'Atualizado'} · {d.interviews} {d.interviews === 1 ? 'entrevista' : 'entrevistas'} · {d.answers} respostas</span>
                <button type="button" onClick={() => analyze.mutate()}><Ic.Refresh />Ler de novo</button></span>}
      </div>
      {err && <p className="obj-st bad bd-err"><Ic.Warning />{err}</p>}
      <p className="obj-int bd-sint">{r.sintese}</p>
      <div className="bd-cols">
        {list('Prioridades do Board · 12 meses', <Ic.Target />, r.prioridades, '—', true)}
        {list('Onde as falas convergem', <Ic.Check />, r.convergencias, d.interviews > 1 ? 'Sem pontos em comum claros.' : 'Uma entrevista só: sem comparação ainda.')}
        {list('Onde divergem', <Ic.Warning />, r.divergencias, d.interviews > 1 ? 'Nenhuma divergência clara nas respostas.' : 'Com mais entrevistas, aparece aqui onde a liderança diverge.')}
      </div>
      <div className="obj-sub">Leituras do ADS</div>
      <Insights items={r.insights} />
      <p className="obj-foot"><Ic.Help />Esta leitura entra no contexto do Strategy, do DNA e do Studio, junto com os documentos e o objetivo.
        {d.note && <em> {d.note}</em>}</p>
    </div>
  );
}
